<template>
  <UIDialog :is-open title="Account Ownership" :z-index size="md" flat @close="handleClose">
    <div class="precision-form">
      <div class="precision-form__items">
        <div class="precision-form__item-title">{{ accountTitle }}</div>
        <div class="precision-form__item-title">Percentage</div>
        <div class="precision-form__item-title">{{ assetTitle }}</div>
        <PrecisionFormItem
          v-for="(item, index) in amountItems"
          :key="index"
          :item
          :index
          :listValues
          :assetName
          :isJustCreated="index >= oldLength"
          @update="handleUpdateOwner"
          @delete="handleDeleteOwner"
        />
        <div v-if="listValues.length > 0" class="precision-form__button">
          <UIButton
            label="Owner"
            variant="secondary"
            size="xs"
            :leading="PlusIcon"
            @click="handleAddOwner"
          />
        </div>
        <div class="precision-form__item-total">Total</div>
        <div class="precision-form__item-total" :class="totalClasses">{{ amountFormatter(totalAmount) }}</div>
      </div>
      <UIButtonsGroup>
        <template #default="{ short }">
          <UIButtonClose :short @click="handleClose" />
          <UIButtonSave
            :short
            label="Update"
            type="submit"
            @click="handleComplete"
          />
        </template>
      </UIButtonsGroup>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { uniqBy } from 'lodash'

import { DropdownListData, ModalClass, TransactionSettings } from '@types'
import { OrganizeFamilyStructureAmountItem, OrganizeFamilyStructurePath } from '../utils/types'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { displayAmount } from '@/entities/transactions/utils/helpers'
import { TransactionAmountRule } from '@/entities/transactions/utils/enums'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { PlusIcon } from '@heroicons/vue/24/outline'
import PrecisionFormItem from './PrecisionFormItem.vue'

type Props = {
  modal: ModalClass<any>
  entrySettings: TransactionSettings['entries'][0]
  assetId: string
  paths: OrganizeFamilyStructurePath[]
  data: DropdownListData[]
}

type Emits = {
  'complete': [data: OrganizeFamilyStructureAmountItem[]]
  'cancel': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsBunchStore = useAssetsBunchStore()

const amountItems = ref<OrganizeFamilyStructureAmountItem[]>([])
const oldLength = ref(0)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const assetName = computed(() => assetsBunchStore.getElementById(props.assetId)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE)

const accountTitle = computed(() => props.entrySettings.account?.title)
const assetTitle = computed(() => props.entrySettings.asset?.title)

const listValues = computed(() => {
  const filtered = props.data.filter(item => item.value !== props.assetId && !amountItems.value.some(i => i.accountId === item.value))
  return uniqBy(filtered, 'value')
})

const totalAmount =  computed(() => {
  return amountItems.value.reduce((acc, value) => {
    return acc + value.amount
  }, 0)
})

const totalClasses = computed(() => ({
  'precision-form__item-total--positive':  totalAmount.value <= 1,
  'precision-form__item-total--negative':  totalAmount.value > 1,
}))

const amountFormatter = (value: number | null | undefined) => {
  const settings = props.entrySettings.amount
  if (!settings) return ''
  const isPercent = settings?.rule === TransactionAmountRule.PERCENT
  return displayAmount(value, isPercent)
}

const handleClose = () => {
  emit('cancel')
  props.modal.close()
}

const handleComplete = () => {
  emit('complete', amountItems.value)
  props.modal.close()
}

const handleAddOwner = () => {
  amountItems.value.push({ accountId: undefined, amount: 0 })
}

const handleUpdateOwner = (index: number, value: OrganizeFamilyStructureAmountItem) => {
  amountItems.value[index] = value
}

const handleDeleteOwner = (index: number) => {
  amountItems.value.splice(index, 1)
}

watch(() => props.paths, value => {
  amountItems.value = uniqBy(value.filter(el => el.path.at(-1) === props.assetId).map(el => ({ accountId: el.path.at(-2), amount: el.amount })), 'accountId')
  oldLength.value = amountItems.value.length
}, { immediate: true })
</script>

<style scoped>
.precision-form {
  @apply pt-4 sm:pt-0;

  &__items {
    @apply grid grid-cols-[1fr_min-content_1fr];

    :deep(> div) {
      @apply px-1.5;

      &:nth-child(3n + 1) {
        @apply pl-default;
      }

      &:nth-child(3n) {
        @apply pr-default;
      }
    }
  }

  &__item-title,
  &__item-total {
    @apply py-2;
    @apply bg-body-gray;
    @apply text-light-text;
    @apply text-sm font-medium;
  }

  &__item-title {
    @apply hidden sm:flex items-center;
    @apply whitespace-normal leading-none;
    @apply mb-1;
  }

  &__item-total {

    &--positive {
      @apply text-gray-950 dark:text-green-400;
    }
    &--negative {
      @apply text-red-600 dark:text-red-400;
    }

    & + & {
      @apply col-span-2;
    }
  }

  &__button {
    @apply col-span-3 mt-2 mb-2;
    @apply !pr-default;

    .ui-button {
      @apply w-full;
    }
  }

  &__button + &__item-total {
    @apply !pl-default;
  }

  .ui-buttons-group {
    @apply py-4 px-default;
  }
}
</style>
